/* eslint-disable jsdoc/require-jsdoc */
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { negate } from '@mycicero/functional-utilities'
import { getCountries, getCountryCallingCode } from 'libphonenumber-js'
import { cond, identity, stubTrue } from 'lodash'
import { Observable, of, throwError } from 'rxjs'
import { catchError, delay, map } from 'rxjs/operators'
import { appConfig } from '../../../brands/_current/config/app-config'
import { environment } from '../../../environments/environment'
import { EnvironmentName } from '../../../environments/environment.enum'
import {
  InvoicingDataFormValue,
  SignUpDataDomicile,
  TermFormValue,
} from '../../sign-up/sign-up-form/sign-up-form.model'
import { MyCiceroWebApiModule } from './my-cicero-web-api.module'
import { AcceptConsentsOutput, isAcceptConsentsOutput } from './shared/accept-consents-output.model'
import { AccountErrorCodeEnum } from './shared/base-login-output.model'
import { BaseOutput, isBaseOutput } from './shared/base-output.model'
import {
  ChangeRegistrationCellAndSendSmsOutput,
  isChangeRegistrationCellAndSendSmsOutput,
} from './shared/change-registration-cell-and-send-sms-output.model'
import { CheckEmailOutput, isCheckEmailOutput } from './shared/check-email-output.model'
import { CheckPhoneOutput, isCheckPhoneOutput } from './shared/check-phone-output.model'
import { ConfirmEmailOTPOutput, isConfirmEmailOTPOutput } from './shared/confirm-emai-otp-output.model'
import {
  ConfirmOtpForAccountMatchingOutput,
  isConfirmOtpForAccountMatchingOutput,
} from './shared/confirm-otp-for-account-matching-output.model'
import { ConfirmPhoneOTPOutput, isConfirmPhoneOTPOutput } from './shared/confirm-phone-otp-output.model'
import { ConfirmPinSmsOutput, isConfirmPinSmsOutput } from './shared/confirm-pin-sms-output.model'
import {
  GetAccountEncryptedDataOutput,
  isGetAccountEncryptedDataOutput,
} from './shared/get-account-encrypted-data-output.model'
import { GetAddressPrefixListOutput, isGetAddressPrefixListOutput } from './shared/get-address-prefix-list-output.model'
import { GetConsentsListOutput, isGetConsentsListOutput } from './shared/get-consents-list-output.model'
import {
  GetInitialConfigurationOutput,
  isGetInitialConfigurationOutput,
} from './shared/get-initial-configuration-output.model'
import {
  GetInvoicingSendingModalitiesOutput,
  isGetInvoicingSendingModalitiesOutput,
} from './shared/get-invoicing-sending-modalities-output.model'
import { ListInternationalDataOutput } from './shared/list-international-data-output.model'
import {
  isFieldValidationErrorResponse,
  isValidationErrorResponse,
  mapValidationErrorOutput,
} from './shared/model-validation-output'
import {
  DisabledAccount,
  OtherLoginError,
  UnexpectedResponsePayload,
  ValidationError,
  ValidationFieldError,
  WrongCredentials,
} from './shared/mycicero-web-api-errors.model'
import { OtpForAccountMatchingType } from './shared/otp-for-account-matching-type.enum'
import { isRedirectOutput, RedirectOutput } from './shared/redirect-output.model'
import { isRegisterAccountOutput, RegisterAccountOutput } from './shared/register-account-output.model'
import { isResendSmsOutput, ResendSmsOutput } from './shared/resend-sms-output.model'
import { isSearchCityOutput, SearchCityOutput } from './shared/search-city-output.model'
import { isSearchCountryOutput, SearchCountryOutput } from './shared/search-country-output.model'
import {
  isSendOtpForAccountMatchingOutput,
  SendOtpForAccountMatchingOutput,
} from './shared/send-otp-for-account-matching-output.model'
import { ExternalProviderEnum } from '../../login/external-provider/external-provider.enum'
import { GenerateHmacSignatureOutput, isGenerateHmacSignatureOutput } from './shared/generate-hmac-signature.model'
import { GetAuthorizeConsentsOutput, isAuthorizeConsentsOutput } from './shared/get-authorize-consents-output.model'
import { AcceptAuthorizeConsentsInputmodel } from './shared/accept-authorize-consents-input.model'
import {
  isStartEmailCertificationOutput,
  StartEmailCertificationApiOutput,
} from './shared/start-email-certification-output.model'

const throwUnexpectedResponseBodyPayload = (responseBody) => {
  throw new UnexpectedResponsePayload('Response was successful but payload is not compatible', {
    responseBody,
  })
}

const withCredentials = { withCredentials: true }
const withoutCredentials = { withCredentials: false }
// eslint-disable-next-line @typescript-eslint/naming-convention
const headers = new HttpHeaders({ 'Content-Type': 'application/json' })

export interface BirthDateInput {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Day: number
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Month: number
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Year: number
}

interface RegisterAccountInput {
  mobileNumber?: string
  password?: string
  passwordAgain?: string
  taxCode?: string
  mobileCountryCode?: string
  dateOfBirth?: BirthDateInput
  domicile?: SignUpDataDomicile
  email?: string
  confirmEmail?: string
  firstName?: string
  gender?: string
  lastName?: string
  consents?: TermFormValue[]
  invoicingData?: Partial<InvoicingDataFormValue>
  redirectUrl?: string
  client?: string
  vatNumber?: string
  companyName?: string
}

interface AcceptConsentsInput {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Consents: ConsentsInput[]
}

interface ExternalLoginInput {
  AuthorizationCode: string
  CodeVerifier: string
  LoginProvider: string
  LoginClient: string
  LoginRedirect: string
}

interface ConsentsInput {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  ConsentCode: string
  // eslint-disable-next-line @typescript-eslint/naming-convention
  ConsentState: boolean
}

@Injectable({
  providedIn: MyCiceroWebApiModule,
})
export class MyCiceroWebApiService {
  constructor(private readonly http: HttpClient) {}

  private static urlForPath(...path: string[]): string {
    return `${appConfig.myCiceroWebApi.urls._base}${path.join('/')}`
  }

  private static handleLoginError<T>(error: unknown): Observable<T> {
    if (error instanceof HttpErrorResponse) {
      switch (error.error?.CodeError) {
        case AccountErrorCodeEnum.wrongCredentials:
          return throwError(() => new WrongCredentials($localize`Impossibile accedere: username o password errata`))
        case AccountErrorCodeEnum.userDisabled:
          return throwError(() => new DisabledAccount($localize`Impossibile accedere: account disattivo`))
        case AccountErrorCodeEnum.unhandled:
        case AccountErrorCodeEnum.genericError:
        case AccountErrorCodeEnum.userDataSynchronisationError:
        default:
          return throwError(() => new OtherLoginError($localize`Impossibile accedere: errore imprevisto`))
      }
    }

    return throwError(() => new OtherLoginError($localize`Impossibile accedere: errore imprevisto`))
  }

  public ssoLogin(
    email: string,
    mobileCountryCode: string,
    mobileNumber: string,
    password: string,
    redirectUrl: string,
    client: string,
    recaptchaToken: string,
  ): Observable<RedirectOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.ssoLogin)
    const body = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Username: email,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Prefix: mobileCountryCode,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Mobile: mobileNumber,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Password: password,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      LoginRedirect: redirectUrl,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      LoginClient: client,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      RememberMe: false,
      'g-recaptcha-response': recaptchaToken,
    }

    // FIXME: Remove when 'g-recaptcha-response' is fixed on BE
    if (environment.name === EnvironmentName.Dev || environment.name === EnvironmentName.Test) {
      body['IsTestRecaptcha'] = true
    }

    return this.http.post<unknown>(url, body, { headers, ...withCredentials }).pipe(
      map(
        cond([
          [negate(isRedirectOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
      catchError((error) => MyCiceroWebApiService.handleLoginError<RedirectOutput>(error)),
    )
  }

  public clientLogin(
    email: string,
    mobileCountryCode: string,
    mobileNumber: string,
    password: string,
    redirectUrl: string,
    client: string,
    captchaValue: string,
  ): Observable<RedirectOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.login)
    const body = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Username: email,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Prefix: mobileCountryCode,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Mobile: mobileNumber,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Password: password,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      LoginRedirect: redirectUrl,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      LoginClient: client,
      'g-recaptcha-response': captchaValue,
    }

    // FIXME: Remove when 'g-recaptcha-response' is fixed on BE
    if (environment.name === EnvironmentName.Dev || environment.name === EnvironmentName.Test) {
      body['IsTestRecaptcha'] = true
    }

    return this.http.post<unknown>(url, body, { headers, ...withCredentials }).pipe(
      map(
        cond([
          [negate(isRedirectOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
      catchError((error) => MyCiceroWebApiService.handleLoginError<RedirectOutput>(error)),
    )
  }

  public resetPassword(email: string, recaptchaToken: string): Observable<BaseOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.resetPassword)
    const body = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Email: email,
      'g-recaptcha-response': recaptchaToken,
    }

    return this.http.post<unknown>(url, body, { headers, ...withoutCredentials }).pipe(
      map(
        cond([
          [negate(isBaseOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
      catchError(cond([[stubTrue, (error) => throwError(() => error)]])),
    )
  }

  public changePassword(newPassword: string, recaptchaToken: string): Observable<BaseOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.changePassword)
    const body = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      NewPassword: newPassword,
      'g-recaptcha-response': recaptchaToken,
    }

    return this.http.post<unknown>(url, body, { headers, ...withCredentials }).pipe(
      map(
        cond([
          [negate(isBaseOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
      catchError(cond([[stubTrue, (error) => throwError(() => error)]])),
    )
  }

  public registerAccount(data: RegisterAccountInput, captchaValue: string): Observable<RegisterAccountOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.registerAccount)
    const billingData = data.invoicingData
    const body = {
      /* eslint-disable @typescript-eslint/naming-convention */
      Name: data.firstName,
      Surname: data.lastName,
      Password: data.password,
      ConfirmPassword: appConfig.signUp.form.fields.passwordAgain.visible ? data.passwordAgain : data.password,
      Prefix: data.mobileCountryCode,
      Mobile: data.mobileNumber,
      BirthDate: data.dateOfBirth,
      DomicileData: !data.domicile
        ? null
        : {
            Address: data.domicile.address,
            City: data.domicile.city,
            Province: data.domicile.region,
            PostalCode: data.domicile.cap,
            State: data.domicile.country,
            HouseNumber: data.domicile.houseNumber,
          },
      Email: data.email,
      ConfirmEmail: appConfig.signUp.form.fields.confirmEmail.visible ? data.confirmEmail : data.email,
      FiscalCode: data.taxCode,
      VatNumber: data.vatNumber,
      CompanyName: data.companyName,
      Consents: data.consents.map((term) => ({ ConsentCode: term.code, ConsentState: term.value })),
      BillingData: !billingData
        ? null
        : {
            Nome: billingData.firstName ?? '',
            Cognome: billingData.lastName ?? '',
            Intestazione: billingData.companyName ?? '',
            Piva: billingData.vatNumber ?? '',
            CodiceFiscale: billingData.taxCode ?? '',
            EmailFatturazione: billingData.email ?? '',
            CodiceNazione: billingData.country ?? '',
            Comune: billingData.cityName ?? billingData.cityFreeTxt ?? '',
            Cap: billingData.cap ?? '',
            Provincia: billingData.region ?? '',
            Indirizzo: billingData.address ?? '',
            Civico: billingData.houseNumber ?? '',
            FatturaElettronica: billingData.electronicInvoicing ?? false,
            Regime_Split_Payment: billingData.splitPayment ?? false,
            PecDestinatario: billingData.pec ?? '',
            CodiceUnivocoDestinatario: billingData.recipientCode ?? '',
            IdToponimo: billingData.addressPrefix ?? null,
            IdModalitaInvioFattura: billingData.sendingMode ?? null,
          },
      LoginClient: data.client,
      LoginRedirect: data.redirectUrl,
      'g-recaptcha-response': captchaValue,
      /* eslint-enable @typescript-eslint/naming-convention */
    }

    // FIXME: Remove when 'g-recaptcha-response' is fixed on BE
    if (environment.name === EnvironmentName.Dev || environment.name === EnvironmentName.Test) {
      body['IsTestRecaptcha'] = true
    }

    return this.http.post<unknown>(url, body, { headers, ...withoutCredentials }).pipe(
      map(
        cond([
          [negate(isRegisterAccountOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
      catchError(
        cond([
          [
            isValidationErrorResponse,
            (response) =>
              throwError(
                () => new ValidationError('Email validation failed', mapValidationErrorOutput(response.error)),
              ),
          ],
          [stubTrue, (error) => throwError(() => error)],
        ]),
      ),
    )
  }

  public validatePassword(password: string, recaptchaValue: string): Observable<BaseOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.validatePassword)
    const body = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Password: password,
      'g-recaptcha-response': recaptchaValue,
    }

    return this.http.post<unknown>(url, body, { headers, ...withoutCredentials }).pipe(
      map(
        cond([
          [negate(isBaseOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
      catchError(
        cond([
          [
            isValidationErrorResponse,
            (response) =>
              throwError(
                () => new ValidationError('Password validation failed', mapValidationErrorOutput(response.error)),
              ),
          ],
          [stubTrue, (error) => throwError(() => error)],
        ]),
      ),
    )
  }

  public validateEmail(email: string, recaptchaToken: string): Observable<BaseOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.validateEmail)
    const body = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Email: email,
      'g-recaptcha-response': recaptchaToken,
    }

    return this.http.post<unknown>(url, body, { headers, ...withoutCredentials }).pipe(
      map(
        cond([
          [negate(isBaseOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
      catchError(
        cond([
          [
            isFieldValidationErrorResponse,
            (response) => throwError(() => new ValidationFieldError('Email validation failed', response.error)),
          ],
          [stubTrue, (error) => throwError(() => error)],
        ]),
      ),
    )
  }

  public validateFiscalCode(fiscalCode: string, recaptchaToken: string): Observable<BaseOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.validateFiscalCode)
    const body = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      FiscalCode: fiscalCode,
      'g-recaptcha-response': recaptchaToken,
    }

    return this.http.post<unknown>(url, body, { headers, ...withoutCredentials }).pipe(
      map(
        cond([
          [negate(isBaseOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
      catchError(
        cond([
          [
            isFieldValidationErrorResponse,
            (response) => throwError(() => new ValidationFieldError('FiscalCode validation failed', response.error)),
          ],
          [stubTrue, (error) => throwError(() => error)],
        ]),
      ),
    )
  }

  public validateMobile(
    mobileCountryCode: string,
    mobileNumber: string,
    recaptchaToken: string,
  ): Observable<BaseOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.validateCell)
    const body = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Cell: mobileNumber,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      PrefixCell: mobileCountryCode,
      'g-recaptcha-response': recaptchaToken,
    }

    return this.http.post<unknown>(url, body, { headers, ...withoutCredentials }).pipe(
      map(
        cond([
          [negate(isBaseOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
      catchError(
        cond([
          [
            isFieldValidationErrorResponse,
            (response) => throwError(() => new ValidationFieldError('Mobile validation failed', response.error)),
          ],
          [stubTrue, (error) => throwError(() => error)],
        ]),
      ),
    )
  }

  public validateVatNumber(vatNumber: string, recaptchaToken: string): Observable<BaseOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.validateVatNumber)
    const body = {
      VatNumber: vatNumber,
      'g-recaptcha-response': recaptchaToken,
    }

    return this.http.post<unknown>(url, body, { headers, ...withoutCredentials }).pipe(
      map(
        cond([
          [negate(isBaseOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
      catchError(
        cond([
          [
            isFieldValidationErrorResponse,
            (response) => throwError(() => new ValidationFieldError('VatNumber validation failed', response.error)),
          ],
          [stubTrue, (error) => throwError(() => error)],
        ]),
      ),
    )
  }

  public getAccountEncryptedData(): Observable<GetAccountEncryptedDataOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.getAccountEncryptedData)
    const body = {}

    return this.http.post<unknown>(url, body, { ...headers, ...withoutCredentials }).pipe(
      map(
        cond([
          [negate(isGetAccountEncryptedDataOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
      catchError(cond([[stubTrue, (error) => throwError(() => error)]])),
    )
  }

  public resendSms(recaptchaValue: string): Observable<ResendSmsOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.resendSms)
    const body = {
      'g-recaptcha-response': recaptchaValue,
    }

    return this.http.post<unknown>(url, body, { headers, ...withoutCredentials }).pipe(
      map(
        cond([
          [negate(isResendSmsOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
      catchError(cond([[stubTrue, (error) => throwError(() => error)]])),
    )
  }

  public resendPhoneOTP(recaptchaValue: string): Observable<CheckPhoneOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.resendPhoneOTP)
    const body = {
      'g-recaptcha-response': recaptchaValue,
    }

    return this.http.post<unknown>(url, body, { headers, ...withoutCredentials }).pipe(
      map(
        cond([
          [negate(isCheckPhoneOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
      catchError(cond([[stubTrue, (error) => throwError(() => error)]])),
    )
  }

  public confirmPhoneOTP(otpCode: string, recaptchaValue: string): Observable<ConfirmPhoneOTPOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.confirmPhoneOTP)
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const body = {
      /* eslint-disable @typescript-eslint/naming-convention */
      OTPCode: otpCode,
      'g-recaptcha-response': recaptchaValue,
      /* eslint-enable @typescript-eslint/naming-convention */
    }

    return this.http.post<unknown>(url, body, { headers, ...withoutCredentials }).pipe(
      map(
        cond([
          [negate(isConfirmPhoneOTPOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
      catchError(cond([[stubTrue, (error) => throwError(() => error)]])),
    )
  }

  public checkEmail(email: string, recaptchaToken: string): Observable<CheckEmailOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.checkEmail)
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const body = {
      /* eslint-disable @typescript-eslint/naming-convention */
      Email: email,
      'g-recaptcha-response': recaptchaToken,
      /* eslint-enable @typescript-eslint/naming-convention */
    }

    return this.http.post<unknown>(url, body, { headers, ...withoutCredentials }).pipe(
      map(
        cond([
          [negate(isCheckEmailOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
      catchError(cond([[stubTrue, (error) => throwError(() => error)]])),
    )
  }

  public resendEmailOTP(recaptchaToken: string): Observable<CheckEmailOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.resendEmailOTP)
    const body = { 'g-recaptcha-response': recaptchaToken }

    return this.http.post<unknown>(url, body, { headers, ...withoutCredentials }).pipe(
      map(
        cond([
          [negate(isCheckEmailOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
      catchError(cond([[stubTrue, (error) => throwError(() => error)]])),
    )
  }

  startEmailCertification(recaptchaValue: string): Observable<StartEmailCertificationApiOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.startEmailCertification)
    return this.http
      .post<StartEmailCertificationApiOutput>(
        url,
        {
          'g-recaptcha-response': recaptchaValue,
        },
        { headers, ...withCredentials },
      )
      .pipe(
        map(
          cond([
            [negate(isStartEmailCertificationOutput), throwUnexpectedResponseBodyPayload],
            [stubTrue, identity],
          ]),
        ),
        catchError(cond([[stubTrue, (error) => throwError(() => error)]])),
      )
  }

  checkEmailCertification(recaptchaValue: string): Observable<RedirectOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.checkEmailCertification)
    return this.http
      .post<RedirectOutput>(
        url,
        {
          'g-recaptcha-response': recaptchaValue,
        },
        { headers, ...withCredentials },
      )
      .pipe(
        map(
          cond([
            [negate(isRedirectOutput), throwUnexpectedResponseBodyPayload],
            [stubTrue, identity],
          ]),
        ),
        catchError(cond([[stubTrue, (error) => throwError(() => error)]])),
      )
  }

  confirmEmailCertification(token: string, userId: string, recaptchaResponse: string): Observable<BaseOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.confirmEmailCertification)
    const params = {
      Token: token,
      UserId: userId,
      'g-recaptcha-response': recaptchaResponse,
    }
    return this.http.post<BaseOutput>(url, params, { headers, ...withCredentials }).pipe(
      map(
        cond([
          [negate(isBaseOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
      catchError(cond([[stubTrue, (error) => throwError(() => error)]])),
    )
  }

  public confirmEmailOTP(otpCode: string, recaptchaValue: string): Observable<ConfirmEmailOTPOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.confirmEmailOTP)
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const body = {
      /* eslint-disable @typescript-eslint/naming-convention */
      OTPCode: otpCode,
      'g-recaptcha-response': recaptchaValue,
      /* eslint-enable @typescript-eslint/naming-convention */
    }

    return this.http.post<unknown>(url, body, { headers, ...withoutCredentials }).pipe(
      map(
        cond([
          [negate(isConfirmEmailOTPOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
      catchError(cond([[stubTrue, (error) => throwError(() => error)]])),
    )
  }

  public completeRegistration(password: string, recaptchaToken: string): Observable<RedirectOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.completeRegistration)
    const body = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Password: password,
      'g-recaptcha-response': recaptchaToken,
    }

    return this.http.post<unknown>(url, body, { headers, ...withCredentials }).pipe(
      map(
        cond([
          [negate(isBaseOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
      catchError(cond([[stubTrue, (error) => throwError(() => error)]])),
    )
  }

  public sendOtpForAccountMatching(
    type: OtpForAccountMatchingType,
    recaptchaToken: string,
  ): Observable<SendOtpForAccountMatchingOutput> {
    const url = MyCiceroWebApiService.urlForPath(`${appConfig.myCiceroWebApi.urls.sendOtpForAccountMatching}/${type}`)

    const body = { 'g-recaptcha-response': recaptchaToken }
    return this.http.post<unknown>(url, body, { headers, ...withCredentials }).pipe(
      map(
        cond([
          [negate(isSendOtpForAccountMatchingOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
      catchError(cond([[stubTrue, (error) => throwError(() => error)]])),
    )
  }

  public confirmOtpForAccountMatching(
    type: OtpForAccountMatchingType,
    otpCode: string,
    recaptchaToken: string,
  ): Observable<ConfirmOtpForAccountMatchingOutput> {
    const url = MyCiceroWebApiService.urlForPath(`${appConfig.myCiceroWebApi.urls.confirmOtpForAccountMatching}`)
    const body = {
      /* eslint-disable @typescript-eslint/naming-convention */
      TipologiaNotifica: type,
      OTPCode: otpCode,
      'g-recaptcha-response': recaptchaToken,
      /* eslint-enable @typescript-eslint/naming-convention */
    }
    return this.http.post<unknown>(url, body, { headers, ...withCredentials }).pipe(
      map(
        cond([
          [negate(isConfirmOtpForAccountMatchingOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
      catchError(cond([[stubTrue, (error) => throwError(() => error)]])),
    )
  }

  public confirmPinSms(confirmCode: string, recaptchaValue: string): Observable<ConfirmPinSmsOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.confirmPinSms)
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const body = { PinSms: confirmCode, 'g-recaptcha-response': recaptchaValue }

    return this.http.post<unknown>(url, body, { headers, ...withoutCredentials }).pipe(
      map(
        cond([
          [negate(isConfirmPinSmsOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
      catchError(cond([[stubTrue, (error) => throwError(() => error)]])),
    )
  }

  public changeRegistrationCellAndSendSms(
    mobileCountryCode: string,
    mobileNumber: string,
    recaptchaToken: string,
  ): Observable<ChangeRegistrationCellAndSendSmsOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.changeRegistrationCellAndSendSms)
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const body = { Cell: mobileNumber, Prefix: mobileCountryCode, 'g-recaptcha-response': recaptchaToken }

    return this.http.post<unknown>(url, body, { headers, ...withoutCredentials }).pipe(
      map(
        cond([
          [negate(isChangeRegistrationCellAndSendSmsOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
      catchError(cond([[stubTrue, (error) => throwError(() => error)]])),
    )
  }

  public listInternationalData(): Observable<ListInternationalDataOutput> {
    return of({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Success: true,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      MessageError: null,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Prefixes: getCountries()
        .filter((country) => !['VA'].includes(country))
        .map((country) => ({
          // eslint-disable-next-line @typescript-eslint/naming-convention
          Country: country,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          CountryCode: getCountryCallingCode(country),
        })),
    }).pipe(delay(1))
  }

  public requestMobileChange(countryCode: string, number: string): Observable<BaseOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.changeMobile)
    const body = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      PrefixCell: countryCode,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Cell: number,
    }

    return this.http.put(url, body, { headers, ...withCredentials }).pipe(
      map(
        cond([
          [negate(isBaseOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
      catchError(cond([[stubTrue, (error) => throwError(() => error)]])),
    )
  }

  public confirmEmail(recaptchaValue: string): Observable<RedirectOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.confirmEmail)
    // eslint-disable-next-line @typescript-eslint/naming-convention

    const body = {
      'g-recaptcha-response': recaptchaValue,
    }

    return this.http.post<unknown>(url, body, { headers, ...withoutCredentials }).pipe(
      map(
        cond([
          [negate(isRedirectOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
      catchError(cond([[stubTrue, (error) => throwError(() => error)]])),
    )
  }

  public checkPhone(
    mobileCountryCode: string,
    mobileNumber: string,
    recaptchaToken: string,
    client: string,
    redirect: string,
  ): Observable<CheckPhoneOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.checkPhone)
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const body = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Phone: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        PhonePrefixes: mobileCountryCode,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        PhoneNumber: mobileNumber,
      },
      // eslint-disable-next-line @typescript-eslint/naming-convention
      LoginClient: client,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      LoginRedirect: redirect,
      'g-recaptcha-response': recaptchaToken,
    }

    return this.http.post<unknown>(url, body, { headers, ...withoutCredentials }).pipe(
      map(
        cond([
          [negate(isCheckPhoneOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
      catchError(cond([[stubTrue, (error) => throwError(() => error)]])),
    )
  }

  public searchCountry(searchString: string): Observable<SearchCountryOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.searchCountry)
    const params = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      TextSearch: searchString,
    }

    return this.http.get(url, { headers, ...withCredentials, params }).pipe(
      map(
        cond([
          [negate(isSearchCountryOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
      catchError(cond([[stubTrue, (error) => throwError(() => error)]])),
    )
  }

  public searchCity(searchString: string): Observable<SearchCityOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.searchCity)
    const params = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      TextSearch: searchString,
    }

    return this.http.get(url, { headers, ...withCredentials, params }).pipe(
      map(
        cond([
          [negate(isSearchCityOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
      catchError(cond([[stubTrue, (error) => throwError(() => error)]])),
    )
  }

  public getInvoicingSendingModalities(): Observable<GetInvoicingSendingModalitiesOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.getInvoicingSendingModalities)

    return this.http.post<unknown>(url, {}, { headers, ...withCredentials }).pipe(
      map(
        cond([
          [negate(isGetInvoicingSendingModalitiesOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
      catchError(cond([[stubTrue, (error) => throwError(() => error)]])),
    )
  }

  public getAddressPrefixList(): Observable<GetAddressPrefixListOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.getAddressPrefixList)

    return this.http.post<unknown>(url, {}, { headers, ...withCredentials }).pipe(
      map(
        cond([
          [negate(isGetAddressPrefixListOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
      catchError(cond([[stubTrue, (error) => throwError(() => error)]])),
    )
  }

  public getConsentsList(): Observable<GetConsentsListOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.getConsentsList)

    return this.http.get(url, { headers, ...withCredentials }).pipe(
      map(
        cond([
          [negate(isGetConsentsListOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
      catchError(cond([[stubTrue, (error) => throwError(() => error)]])),
    )
  }

  public acceptConsents(data: AcceptConsentsInput): Observable<AcceptConsentsOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.acceptConsents)

    return this.http.post<unknown>(url, data, { headers, ...withCredentials }).pipe(
      map(
        cond([
          [negate(isAcceptConsentsOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
      catchError(cond([[stubTrue, (error) => throwError(() => error)]])),
    )
  }

  public insertPhone(phonePrefix: string, phoneNumber: string, recaptchaToken: string): Observable<RedirectOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.insertPhone)

    return this.http
      .post<unknown>(
        url,
        {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          Prefix: phonePrefix,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          Cell: phoneNumber,
          'g-recaptcha-response': recaptchaToken,
        },
        { headers, ...withCredentials },
      )
      .pipe(
        map(
          cond([
            [negate(isRedirectOutput), throwUnexpectedResponseBodyPayload],
            [stubTrue, identity],
          ]),
        ),
        catchError(cond([[stubTrue, (error) => throwError(() => error)]])),
      )
  }

  public getInitialConfiguration(): Observable<GetInitialConfigurationOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.initialConfiguration)
    return this.http.get<GetInitialConfigurationOutput>(url, { ...withCredentials }).pipe(
      map(
        cond([
          [negate(isGetInitialConfigurationOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
      catchError(cond([[stubTrue, (error) => throwError(() => error)]])),
    )
  }

  public externalLogin(externalLoginInput: ExternalLoginInput, recaptchaToken: string): Observable<RedirectOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.externalLogin)
    const body = {
      ...externalLoginInput,
      'g-recaptcha-response': recaptchaToken,
    }

    return this.http.post<unknown>(url, body, { headers, ...withCredentials }).pipe(
      map(
        cond([
          [negate(isRedirectOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
    )
  }

  public generateHmacSignature(
    redirectBackUrl: string,
    externalLoginProvider: ExternalProviderEnum,
  ): Observable<GenerateHmacSignatureOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.generateHmacSignature)
    const body = {
      UrlRedirect: redirectBackUrl,
      LoginProvider: externalLoginProvider,
    }

    return this.http.post<unknown>(url, body, { headers, ...withCredentials }).pipe(
      map(
        cond([
          [negate(isGenerateHmacSignatureOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
    )
  }

  public getAuthorizeConsents(): Observable<GetAuthorizeConsentsOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.getAuthorizeConsents)
    return this.http.get<GetAuthorizeConsentsOutput>(url, { headers, ...withCredentials }).pipe(
      map(
        cond([
          [negate(isAuthorizeConsentsOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
    )
  }

  public acceptAuthorizeConsents(data: AcceptAuthorizeConsentsInputmodel): Observable<BaseOutput> {
    const url = MyCiceroWebApiService.urlForPath(appConfig.myCiceroWebApi.urls.acceptAuthorizeConsents)
    return this.http.post<unknown>(url, data, { headers, ...withCredentials }).pipe(
      map(
        cond([
          [negate(isBaseOutput), throwUnexpectedResponseBodyPayload],
          [stubTrue, identity],
        ]),
      ),
    )
  }
}
